import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmText: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ConfirmDialog({
  title,
  message,
  confirmText,
  confirmLabel,
  danger = true,
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = useState('');
  const isMatch = inputValue === confirmText;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isMatch && !loading) onConfirm();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md flex flex-col bg-dbgo-modal border rounded-lg shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0">
          <div className="flex items-center gap-2">
            <AlertTriangle
              size={18}
              className={danger ? 'text-accent-red' : 'text-accent-yellow'}
            />
            <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          <p className="text-xs text-text-secondary leading-relaxed">{message}</p>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              {confirmLabel ?? `Type '${confirmText}' to confirm`}
            </label>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              autoFocus
              placeholder={confirmText}
              className="w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors font-mono"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-3 py-[6px] text-xs font-medium text-text-secondary hover:text-text-primary rounded-sm hover:bg-dbgo-tab-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isMatch || loading}
            className={`flex items-center gap-1.5 px-4 py-[6px] text-xs font-medium text-white rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              danger
                ? 'bg-accent-red hover:bg-accent-red/90'
                : 'bg-accent-blue hover:bg-accent-blue/90'
            }`}
          >
            {loading && <Loader2 size={12} className="animate-spin" />}
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </form>
    </div>
  );
}
